// Pure, coverage-testable White-Space Discovery logic — Story #4937 Stream 2.
// No AWS, no DOM, no canvas: everything here is scoring/layout/polling math so
// it can be exercised under jsdom without a live backend (vitest.config.ts
// coverage floors 68/61/60/70 — this module is where that coverage lives).
//
// Region-shape functions below match the REAL deployed contract in
// lambda/innovateflow-api/whitespace/handler.py's `_score_regions` — raw WOS
// bands (sparsity/adjacency/momentum/enablement/wos_score + patent/academic
// record ids), not the Bedrock-narrated title/provenance-chip shape the
// screen3 mockup shows. whitespace/synthesis.py exists but is not wired into
// any route yet — that is a real, documented gap (Task 2d), not invented here.
import type {
  WhitespaceRegion,
  WhitespaceRegionsResponse,
  WhitespaceScanResult,
  WhitespaceScanStatus,
  WhitespaceWatch,
  PortfolioSynergyCell,
  PortfolioSynergyPairScore,
  PortfolioMapLayout,
  PortfolioScoreRegion,
  PortfolioTopologyFrontierEdge,
  PortfolioWatch,
  PortfolioEntitlementResponse,
  PortfolioTopologyResponse,
  PortfolioScoreResponse,
  PortfolioSynergyResponse,
  OverlapGroup,
  OverlapTier,
} from '../types/whitespace'

// ---------------------------------------------------------------------------
// Explorer card v2 — Story #5305 (Task #5325). Design §5.1's distance ladder,
// floor-integer printed ranges, verdict-first cards, empty-span collapse and
// watch fan-out. band_key/band_name/verdict are server-templated
// (whitespace/scoring.py, Stream 10a) — the fallbacks below exist only for a
// cache row written before that deploy (24h TTL), never as a second,
// independently-worded copy of the server's own text.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Region mapping + sorting (Opportunity Explorer — Task 2a/2b)
// ---------------------------------------------------------------------------

/** Highest WOS score first — ties broken by band lower-edge so ordering is deterministic. */
export function sortRegionsByScore(regions: WhitespaceRegion[]): WhitespaceRegion[] {
  return [...regions].sort((a, b) => b.wos_score - a.wos_score || a.band[0] - b.band[0])
}

/** Score → integer percent string for the ring/bar labels (e.g. 86.6 -> "87"). */
export function formatWhitespaceScore(score: number): string {
  return String(Math.round(Math.max(0, Math.min(100, score))))
}

/** The conic-gradient stop for a score ring, matching the mockup's amber/gold fill. */
export function scoreRingGradient(score: number): string {
  const pct = Math.round(Math.max(0, Math.min(100, score)))
  return `conic-gradient(#FBBF24 0 ${pct}%, #E2E8F0 ${pct}% 100%)`
}

/** A short, deterministic label for a region — no Bedrock title exists yet,
 * so this names the band range plainly rather than inventing prose. */
export function regionLabel(region: WhitespaceRegion): string {
  const [lo, hi] = region.band
  return `Similarity ${lo.toFixed(2)}–${hi.toFixed(2)}`
}

/** A plain, data-derived summary sentence — real counts only, never invented
 * narrative. Bedrock synthesis (whitespace/synthesis.py) is unwired; this is
 * the honest stand-in until it lands on the scan route. */
export function regionSummary(region: WhitespaceRegion): string {
  const total = region.patent_count + region.academic_count
  if (total === 0) {
    return 'No prior art found in this band across either corpus — a genuinely white region.'
  }
  const parts: string[] = []
  if (region.patent_count > 0) parts.push(`${region.patent_count} patent record${region.patent_count === 1 ? '' : 's'}`)
  if (region.academic_count > 0) parts.push(`${region.academic_count} academic record${region.academic_count === 1 ? '' : 's'}`)
  const saturation = region.saturated ? ' (retrieval saturated — this may be an undercount)' : ''
  return `${parts.join(' and ')} found in this band${saturation}.`
}

/** Chips derived from real fields only — no invented "combination gap" text
 * (there is no combination-matrix data behind this region). */
export function regionChips(region: WhitespaceRegion): string[] {
  const chips: string[] = []
  if (region.patent_count === 0 && region.academic_count === 0) chips.push('Fully white')
  if (region.saturated) chips.push('Retrieval saturated')
  if (region.academic_count > 0 && region.patent_count === 0) chips.push('Journal-led signal')
  return chips
}

/** Stable identity for a region's band — the same key OpportunityExplorer's
 * card list and the watchlist use to recognise "this is the same region". */
export function regionBandKey(band: [number, number]): string {
  return `${band[0]}-${band[1]}`
}

/** Which corpus/corpora this region's evidence actually came from — real
 * counts only, never a fabricated single-corpus label. Feeds the `corpus`
 * field POST /whitespace/watch stores (watchlist.py just persists the
 * string it's given; it doesn't validate against an enum). */
export function regionCorpusLabel(region: WhitespaceRegion): string {
  const parts: string[] = []
  if (region.patent_count > 0) parts.push('patents')
  if (region.academic_count > 0) parts.push('academic')
  return parts.length > 0 ? parts.join('+') : 'patents'
}

/** The record ids to store on a new watch row — same boundary ids the card
 * already shows, capped the same way watchlist.py caps them server-side
 * (MAX_WATCH_RECORD_IDS=50) so the client never sends more than the backend
 * will keep. */
export function regionWatchRecordIds(region: WhitespaceRegion, max = 50): string[] {
  return [...region.patent_record_ids, ...region.academic_record_ids].slice(0, max)
}

/** Maps a project's watch rows onto a band-key -> watch_id lookup, so the UI
 * can tell which regions are already watched. Only this project's rows
 * count — list_watches can return rows for any project the caller can still
 * view (amendment D), and a region card only ever represents one project. */
export function watchIdsByBand(watches: WhitespaceWatch[], projectId: string): Record<string, string> {
  const map: Record<string, string> = {}
  for (const w of watches) {
    if (w.project_id !== projectId) continue
    map[regionBandKey(w.band)] = w.watch_id
  }
  return map
}

// ---------------------------------------------------------------------------
// Explorer card v2 — Story #5305 §5.1 (Task #5325). The distance ladder,
// floor-integer printed ranges, verdict-first text, empty-span collapse and
// the watch fan-out plan. Pure formatting/grouping only — WhiteSpaceTab owns
// the actual API calls a fan-out plan describes.
// ---------------------------------------------------------------------------

/** scoring.py's BAND_DISPLAY, verbatim and in the SAME closest-to-furthest
 * order — the one ladder every band name/collapse/range computation below
 * walks. Never reordered locally: a caller that needs "furthest first"
 * reverses a copy rather than redefining the order. */
export const BAND_LADDER: { key: string; name: string }[] = [
  { key: 'very_close', name: 'Very close' },
  { key: 'close', name: 'Close' },
  { key: 'moderately_close', name: 'Moderately close' },
  { key: 'further_out', name: 'Further out' },
  { key: 'far_afield', name: 'Far afield' },
]

/** Position in BAND_LADDER (0 = closest), or -1 for an unrecognised key. */
export function bandLadderIndex(bandKey: string | undefined): number {
  if (!bandKey) return -1
  return BAND_LADDER.findIndex((b) => b.key === bandKey)
}

/** Same edges as handler.py's ANNULUS_BANDS, used ONLY to recover
 * band_key/band_name/verdict for a cache row written before Stream 10a
 * (24h TTL) — every fresh scan already carries these fields from the
 * server and this table is never consulted for one. Matched by the raw
 * band's lower edge, which is unique per band. */
const ANNULUS_BAND_LOWER_EDGES = [0.85, 0.72, 0.62, 0.45, 0.0]

function fallbackBandIndex(band: [number, number]): number {
  const idx = ANNULUS_BAND_LOWER_EDGES.findIndex((lo) => Math.abs(lo - band[0]) < 1e-6)
  return idx
}

/** The band's display name — the server's own `band_name` when present,
 * else derived from the raw band edges for a pre-10a cache row. Never
 * fabricates a sixth name: an edge that matches nothing falls back to the
 * old plain range label rather than guessing. */
export function regionBandName(region: WhitespaceRegion): string {
  if (region.band_name) return region.band_name
  const idx = fallbackBandIndex(region.band)
  return idx >= 0 ? BAND_LADDER[idx].name : regionLabel(region)
}

/** The band's ladder key — server-provided, or the same fallback match
 * regionBandName() uses. Used to order/collapse regions by ladder
 * position, independent of the wos_score sort the cards are ranked by. */
export function regionBandLadderKey(region: WhitespaceRegion): string | undefined {
  if (region.band_key) return region.band_key
  const idx = fallbackBandIndex(region.band)
  return idx >= 0 ? BAND_LADDER[idx].key : undefined
}

/** Deterministic verdict sentence — the server's own `verdict` (scoring.py's
 * region_verdict(), so app and exports always agree) when present, else the
 * identical template evaluated client-side for a pre-10a cache row. */
export function regionVerdict(region: WhitespaceRegion): string {
  if (region.verdict) return region.verdict
  const { patent_count, academic_count } = region
  if (patent_count === 0 && academic_count === 0) {
    return "Nothing recorded — but nothing published proves it's feasible either, "
      + 'which is why this scores below areas with published research but no patent filings yet.'
  }
  if (patent_count === 0) return 'Published but never filed — open to claim'
  if (academic_count === 0) {
    return `Crowded — ${patent_count} ${patent_count === 1 ? 'filing' : 'filings'} already here`
  }
  return `Active — ${patent_count} ${patent_count === 1 ? 'filing' : 'filings'} and `
    + `${academic_count} ${academic_count === 1 ? 'publication' : 'publications'} here`
}

/** design §5.1's single "floor of 100 × the value" rule — the ONE place a
 * raw 0-1 cosine/share becomes the displayed 0-100 integer every surface
 * must agree on (band membership, printed ranges, and single-value anchors
 * like a probe's "Closest existing record: N" all derive from this). The
 * +1e-9 guards against float noise landing just under a boundary (e.g.
 * 0.9999999999 for a value that should floor to exactly 100). */
export function floorToDisplayInteger(value: number): number {
  return Math.floor(value * 100 + 1e-9)
}

/** design §5.1: "the printed ranges are non-overlapping integers, and band
 * membership is computed on the displayed integer — the floor of 100 × the
 * value". A band's raw upper edge is the NEXT band's lower edge, so the
 * printed upper bound is one less than floor(100 × hi) — except the top
 * band (hi === 1), which prints inclusively up to 100. */
export function bandPrintedRange(band: [number, number]): [number, number] {
  const lo = floorToDisplayInteger(band[0])
  const hiFloor = floorToDisplayInteger(band[1])
  const hi = band[1] >= 1 ? hiFloor : hiFloor - 1
  return [lo, hi]
}

/** The fixed caption every non-collapsed band card carries (design §5.1) —
 * band names are distance words, never place words, because two records in
 * one band can sit on opposite sides of the idea. */
export const BAND_CARD_CAPTION = "records at this distance from your project's centre — in any direction"

/** design §5.1's fixed anchor phrasing ("Similarity to your project: NN"),
 * printed as a range instead of a single value because a band card names a
 * span, not a point — this must be the literal phrasing, not a paraphrase,
 * since it is one of the design's four fixed anchor forms. */
export function bandSimilarityAnchor(range: [number, number]): string {
  return `Similarity to your project: ${range[0]}–${range[1]}`
}

/** A region counts as empty for collapse purposes when neither corpus has
 * any hit in it — the exact condition region_verdict()'s first branch
 * templates ("Nothing recorded..."), never a score threshold. */
export function isEmptyRegion(region: WhitespaceRegion): boolean {
  return region.patent_count === 0 && region.academic_count === 0
}

/** One explorer card's worth of regions — length 1 for an ordinary card,
 * length 2+ for a collapsed empty-span card. Always ladder-ordered
 * (closest member first). */
export interface ExplorerCardGroup {
  regions: WhitespaceRegion[]
}

/** design §5.1: "Adjacent all-empty bands collapse into one card". Orders
 * regions by ladder position (independent of the wos_score sort they arrive
 * in), then folds any RUN of 2+ consecutive empty regions into one group —
 * a lone empty region between two occupied ones stays its own card, exactly
 * as the design's own worked example does (Far afield stays separate from
 * the Very close/Close/Moderately close span in the mockup). A region whose
 * band key cannot be resolved at all sorts to the end, each in its own
 * group, rather than guessing where it collapses. Groups are finally
 * re-sorted by score descending (identical within an empty run, so this is
 * a stable no-op there) to preserve the "ranked by opportunity" ordering. */
export function groupRegionsForExplorer(regions: WhitespaceRegion[]): ExplorerCardGroup[] {
  const ordered = [...regions].sort((a, b) => {
    const ai = bandLadderIndex(regionBandLadderKey(a))
    const bi = bandLadderIndex(regionBandLadderKey(b))
    if (ai === -1 && bi === -1) return 0
    if (ai === -1) return 1
    if (bi === -1) return -1
    return ai - bi
  })

  const groups: WhitespaceRegion[][] = []
  for (const region of ordered) {
    const prevGroup = groups[groups.length - 1]
    const canExtend = prevGroup
      && isEmptyRegion(region)
      && isEmptyRegion(prevGroup[prevGroup.length - 1])
      && bandLadderIndex(regionBandLadderKey(region)) !== -1
    if (canExtend) {
      prevGroup.push(region)
    } else {
      groups.push([region])
    }
  }

  return groups
    .map((g) => ({ regions: g }))
    .sort((a, b) => b.regions[0].wos_score - a.regions[0].wos_score)
}

const COLLAPSE_COUNT_WORDS: Record<number, string> = { 2: 'two', 3: 'three', 4: 'four', 5: 'five' }

/** "three distance rings, combined — all empty" (mockup screenA's collapsed
 * card caption), the collapsed sibling of BAND_CARD_CAPTION. */
export function collapsedGroupCaption(memberCount: number): string {
  const word = COLLAPSE_COUNT_WORDS[memberCount] ?? String(memberCount)
  return `${word} distance ring${memberCount === 1 ? '' : 's'}, combined — all empty`
}

/** "Very close → Far afield" (or a single name when the run is one band) —
 * the collapsed card's header, naming the span's two ends; the verdict line
 * (collapsedGroupVerdict) lists every member by name so nothing is lost by
 * the header's brevity. */
export function collapsedGroupHeaderName(regions: WhitespaceRegion[]): string {
  const first = regionBandName(regions[0])
  const last = regionBandName(regions[regions.length - 1])
  return first === last ? first : `${first} → ${last}`
}

/** Oxford-comma-less "A, B and C" join — design §5.1's own example
 * ("Nothing recorded across Very close, Close and Moderately close"). */
export function joinWithAnd(items: string[]): string {
  if (items.length === 0) return ''
  if (items.length === 1) return items[0]
  if (items.length === 2) return `${items[0]} and ${items[1]}`
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

/** design §5.1, verbatim pattern: "Nothing recorded across Very close,
 * Close and Moderately close" — the collapsed card's verdict line. */
export function collapsedGroupVerdict(regions: WhitespaceRegion[]): string {
  return `Nothing recorded across ${joinWithAnd(regions.map(regionBandName))}`
}

/** The collapsed card's printed range: the span from the furthest member's
 * low edge to the closest member's high edge — regions must be passed in
 * ladder order (closest first), the same order groupRegionsForExplorer()
 * already produces. */
export function collapsedGroupPrintedRange(regions: WhitespaceRegion[]): [number, number] {
  const ranges = regions.map((r) => bandPrintedRange(r.band))
  return [Math.min(...ranges.map(([lo]) => lo)), Math.max(...ranges.map(([, hi]) => hi))]
}

/** How many of a group's member bands are already watched, out of the
 * group's total — the real numbers behind "Watching 3 of 3 bands". */
export interface CollapsedWatchState {
  watchedCount: number
  totalCount: number
  isFullyWatched: boolean
  isPartiallyWatched: boolean
}

export function collapsedWatchState(
  regions: WhitespaceRegion[],
  watchMap: Record<string, string>,
): CollapsedWatchState {
  const totalCount = regions.length
  const watchedCount = regions.filter((r) => !!watchMap[regionBandKey(r.band)]).length
  return {
    watchedCount,
    totalCount,
    isFullyWatched: totalCount > 0 && watchedCount === totalCount,
    isPartiallyWatched: watchedCount > 0 && watchedCount < totalCount,
  }
}

/** design §5.1: "aggregate state shown — 'Watching 3 of 3 bands'". */
export function collapsedWatchLabel(state: CollapsedWatchState): string {
  if (state.isFullyWatched) return `★ Watching ${state.watchedCount} of ${state.totalCount} bands`
  if (state.isPartiallyWatched) return `☆ Watch remaining bands (${state.watchedCount} of ${state.totalCount} watched)`
  return `☆ Watch all ${state.totalCount} rings`
}

/** What a click on the collapsed card's watch button should DO, given the
 * group's current state — pure so WhiteSpaceTab's mutation is a thin
 * executor rather than re-deriving this logic inline. "Idempotent": only
 * the bands NOT already watched are included in toCreate, so re-clicking a
 * partially-watched group never double-watches a band. "All-or-rollback"
 * is the caller's job (create every band in toCreate, and on any failure
 * delete whichever of THIS call's own creates already succeeded) — this
 * function only describes the target set, never performs the calls. */
export interface WatchFanOutPlan {
  action: 'watch' | 'unwatch'
  /** Regions needing a NEW watch row (action === 'watch' only). */
  toCreate: WhitespaceRegion[]
  /** Existing watch_ids to delete (action === 'unwatch' only). */
  toDelete: string[]
}

export function planCollapsedWatchToggle(
  regions: WhitespaceRegion[],
  watchMap: Record<string, string>,
): WatchFanOutPlan {
  const state = collapsedWatchState(regions, watchMap)
  if (state.isFullyWatched) {
    return {
      action: 'unwatch',
      toCreate: [],
      toDelete: regions.map((r) => watchMap[regionBandKey(r.band)]).filter((id): id is string => !!id),
    }
  }
  return {
    action: 'watch',
    toCreate: regions.filter((r) => !watchMap[regionBandKey(r.band)]),
    toDelete: [],
  }
}

/** The fixed rank-strip sentence (design §5.1 / mockup screenA) — one
 * source of truth so the Explorer and any export built from the same
 * ranking agree word for word. */
export const EXPLORER_RANK_STRIP_TEXT =
  'Ranked by opportunity, not emptiness — a region with supporting literature but no filings outranks a totally silent one.'

/** One-line, real-count-only explanations for each "Why this score?"
 * sub-score bar (design §5.1: "each with a one-line meaning and the raw
 * count that produced it"). Never a fabricated number — momentum has no
 * single raw count behind it (scoring.momentum_from_publication_dates is a
 * recency computation, not a tally), so its line names the real signal
 * instead of inventing one. */
export function subScoreExplanation(
  key: 'sparsity' | 'adjacency' | 'momentum' | 'enablement',
  region: WhitespaceRegion,
): string {
  const total = region.patent_count + region.academic_count
  switch (key) {
    case 'sparsity':
      return total === 0 ? 'no records found nearby' : `${total} record${total === 1 ? '' : 's'} found nearby`
    case 'adjacency':
      return region.patent_count === 0
        ? 'no patent filings at this distance'
        : `${region.patent_count} patent ${region.patent_count === 1 ? 'filing' : 'filings'} at this distance`
    case 'momentum':
      return 'based on real filing recency in this band'
    case 'enablement':
      return region.academic_count === 0
        ? 'no published research at this distance'
        : `${region.academic_count} paper${region.academic_count === 1 ? '' : 's'} show${region.academic_count === 1 ? 's' : ''} the science works`
    default:
      return ''
  }
}

/** design §5.2: "Before the first scan, every sub-view shows exactly one
 * call-to-action — the scan — with a one-line preview of what that view
 * will show." One title/preview pair per sub-view, all pointing at the
 * SAME "Run scan" action — never each view's own separate build button. */
export interface FirstRunCtaCopy {
  title: string
  description: string
}

export function firstRunCtaCopy(view: 'explorer' | 'matrix' | 'map' | '3d'): FirstRunCtaCopy {
  switch (view) {
    case 'matrix':
      return {
        title: 'No regions scored yet — run a scan',
        description: 'Once scored, this view will show feature × domain density across the corpus — where combinations are crowded and where they are white.',
      }
    case 'map':
      return {
        title: 'No regions scored yet — run a scan',
        description: 'Once scored, this view will place your project and the nearby corpus on a 2-D map you can probe.',
      }
    case '3d':
      return {
        title: 'No regions scored yet — run a scan',
        description: 'Once scored, this view will render the same landscape in 3-D, with the corpus positioned around your project.',
      }
    case 'explorer':
    default:
      return {
        title: 'No regions scored yet — run a scan',
        description: 'Score sparse combinations and adjacent opportunities against the loaded corpus for this project.',
      }
  }
}

/** One of handler.py's SCAN_STAGE_* fixed strings, sentence-cased for
 * display — 'scanning corpus' -> 'Scanning corpus'. Real server text only:
 * never invents a stage for a missing/unrecognised value. */
export function formatScanStage(stage: string | null | undefined): string | null {
  if (!stage) return null
  return stage.charAt(0).toUpperCase() + stage.slice(1)
}

// ---------------------------------------------------------------------------
// Portfolio Watch panel (Story #5115 Stream 7e, Task #5138)
// ---------------------------------------------------------------------------

/** The real member-project scope of a portfolio entitlement — own_org
 * projects plus every granted (non-owned) project id, deduped and sorted
 * for a stable UI order. Mirrors what portfolio_watchlist.py's create_watch
 * itself authorizes against (aggregator_can_read per member project); this
 * is only ever a client-side proposal, the server re-checks every id. */
export function portfolioEntitlementMemberProjectIds(
  entitlement: Pick<PortfolioEntitlementResponse, 'own_org_project_ids' | 'granted_projects'>,
): string[] {
  return Array.from(
    new Set([...entitlement.own_org_project_ids, ...Object.keys(entitlement.granted_projects)]),
  ).sort()
}

/** The watch rows one view should show — scope-aware in BOTH directions.
 *
 * WHY THE OLD EARLY-RETURN-EMPTY ON A NULL AGGREGATOR ID WAS A BUG, rather
 * than a safe default: an inventor who belongs to no aggregator has
 * activeAggregatorId === null, so that line blanked their watch list whatever
 * the backend returned. Watch is
 * named in the Goal of story #5648, and this was the one line standing between
 * that user and their own data.
 *
 * WHY BOTH DIRECTIONS MATTER. Own and aggregator rows share ONE table keyed
 * owner_id/watch_id (portfolio_watchlist.py), and list_watches returns the same
 * rows to both calls — measured on the deployed dev API 5 Sep 2026: bob's one
 * own-scope row came back from `?scope=own` AND from the un-scoped call. So the
 * server does not separate these views and the client must:
 *   - own scope (no aggregator id): the rows stamped scope === 'own', and
 *     NEVER an aggregator's row;
 *   - aggregator scope: that aggregator's rows, and NEVER an own-scope row.
 * The second half is what stops an inventor's private watch appearing in an
 * aggregator's panel — it matters most in the case the story names, a billing
 * owner whose own sub doubles as an aggregator id.
 *
 * A row with no `scope` predates A1(e) and reads as 'aggregator', which is the
 * same default portfolio_watchlist.py:326 applies server-side. */
export function portfolioWatchesForAggregator(watches: PortfolioWatch[], aggregatorId: string | null): PortfolioWatch[] {
  const isOwnRow = (w: PortfolioWatch) => (w.scope ?? 'aggregator') === 'own'
  if (!aggregatorId) return watches.filter(isOwnRow)
  return watches.filter((w) => !isOwnRow(w) && w.aggregator_id === aggregatorId)
}

/** Story #5648 (Graduated UX, contract row S4) — Figure 5's "Shape at a
 * glance" tile strip, derived at OWN scope from the same three payloads the
 * aggregator Atlas already reads (portfolio_topology, portfolio_score,
 * portfolio_synergy at ?scope=own).
 *
 * The whole point of the type below is the difference between a measured
 * ZERO and a number nobody has computed. bob really does have 0 holes and
 * 6 frontier directions today; portfolio_synergy at own scope still 404s
 * (lane A1's remaining route body), so its tile must say so rather than
 * print 0 — a 0 standing in for "not computed" is the exact dishonesty the
 * dashboard rules ban, and "--" is banned outright. */
export interface OwnScopeShapeTile {
  label: string
  /** null means NOT COMPUTED — the caller renders `note` instead of a
   * number. A real measured zero is the number 0, never null. */
  value: number | null
  /** Why this tile reads the way it does, in the design's own words where
   * Figure 5 gives them. Always present when `value` is null; also present
   * for a measured zero that has a stated reason. */
  note?: string
}

export function buildOwnScopeShapeTiles(input: {
  /** The OWNED inventions the roster renders — 9 for bob — not the
   * entitlement id count (10), which includes the one project shared in to
   * him. Figure 5's first tile counts what is drawable as yours. */
  ownedInventionCount: number
  topology?: Pick<PortfolioTopologyResponse, 'holes' | 'bridges' | 'frontier_edges' | 'discarded_candidate_count'>
  score?: Pick<PortfolioScoreResponse, 'discarded_candidate_count'>
  synergy?: Pick<PortfolioSynergyResponse, 'cross_project_cell_count'>
}): OwnScopeShapeTile[] {
  const { ownedInventionCount, topology, score, synergy } = input
  const tooFewInventions = ownedInventionCount < 2
  const needsTwo = 'Needs at least two inventions'
  const notComputed = 'Not computed yet'

  const topologyTile = (label: string, value: number | undefined): OwnScopeShapeTile => {
    if (value === undefined) return { label, value: null, note: notComputed }
    if (value === 0 && tooFewInventions) return { label, value: 0, note: needsTwo }
    return { label, value }
  }

  // score.discarded_candidate_count is the same counter topology returns;
  // whichever payload arrived is authoritative, and they agree when both did.
  const discarded = topology?.discarded_candidate_count ?? score?.discarded_candidate_count

  return [
    { label: 'Inventions', value: ownedInventionCount },
    topologyTile('Holes', topology?.holes.length),
    topologyTile('Bridges', topology?.bridges.length),
    topologyTile('Frontier directions', topology?.frontier_edges.length),
    synergy === undefined
      ? { label: 'Synergy cells', value: null, note: notComputed }
      : topologyTile('Synergy cells', synergy.cross_project_cell_count),
    discarded === undefined
      ? { label: '2-D artifacts discarded', value: null, note: notComputed }
      : { label: '2-D artifacts discarded', value: discarded },
  ]
}

/** Which of this aggregator's portfolio watches (if any) already covers the
 * given corpus + band — the same "is this region already watched" question
 * watchIdsByBand answers for the single-project watchlist, but keyed by
 * corpus+band since a portfolio watch has no single project_id to key on. */
export function findPortfolioWatch(
  watches: PortfolioWatch[],
  corpus: string,
  band: [number, number],
): PortfolioWatch | undefined {
  return watches.find((w) => w.corpus === corpus && w.band[0] === band[0] && w.band[1] === band[1])
}

/** Maps a raw scan-result payload into the shape the regions view renders. Pure —
 * no fetch, no polling — so response-mapping bugs ("undefined undefined") are
 * caught by vitest against a fixture, not discovered live. */
export function mapScanResultToRegionsResponse(
  result: WhitespaceScanResult,
  projectId: string,
): WhitespaceRegionsResponse {
  const regions = sortRegionsByScore(result.regions ?? [])
  return {
    project_id: projectId,
    run_id: result.run_id,
    status: result.status,
    completed_at: result.completed_at ?? null,
    regions,
    corpus_coverage_note: corpusCoverageNote(regions.length),
  }
}

/** The one fact behind every "scored relative to the loaded corpus"
 * honesty caveat in the app (Bug #5336) — helpContent.ts's ws-overview and
 * ws-how-scored articles and this file's own corpusCoverageNote() live
 * caption each explain the same idea in their own words for their own
 * context, but all three end on this exact clause. Shared here so it
 * cannot drift into three independently-worded (and eventually
 * contradictory) copies again. */
export const CORPUS_COVERAGE_GROWS_CLAUSE = 'as more patents and journals are ingested'

/** The "relative to loaded corpus" caveat the AC requires ALWAYS be visible. */
export function corpusCoverageNote(regionCount: number): string {
  return regionCount > 0
    ? `Scored relative to the loaded corpus — coverage grows ${CORPUS_COVERAGE_GROWS_CLAUSE}.`
    : 'No regions scored yet — results are always relative to the loaded corpus, not the full prior-art universe.'
}

// ---------------------------------------------------------------------------
// Scan polling state machine (Task 2a) — async from day one per amendment C.
// ---------------------------------------------------------------------------

export interface PollDecision {
  action: 'poll' | 'stop'
  /** ms to wait before the next poll; present only when action === 'poll'. */
  delayMs?: number
}

const TERMINAL_STATUSES: WhitespaceScanStatus[] = ['complete', 'failed']
// Exported so WhiteSpaceTab's own timeout heartbeat (Task #5325
// "silent-poll-timeout fix") shares the exact same numbers refetchInterval
// uses, rather than a second, driftable copy.
export const POLL_INTERVAL_MS = 2000
export const POLL_TIMEOUT_MS = 120_000

export function isTerminalScanStatus(status: WhitespaceScanStatus): boolean {
  return TERMINAL_STATUSES.includes(status)
}

/** Given the current status and how long we've been polling, decide the next
 * step. Pure function so the whole state machine — including the timeout —
 * is unit-tested without a fake timer touching a real setInterval. */
export function nextPollDecision(status: WhitespaceScanStatus, elapsedMs: number): PollDecision {
  if (isTerminalScanStatus(status)) return { action: 'stop' }
  if (elapsedMs >= POLL_TIMEOUT_MS) return { action: 'stop' }
  return { action: 'poll', delayMs: POLL_INTERVAL_MS }
}

// ---------------------------------------------------------------------------
// Combination Matrix layout/intensity math (Task 2c)
//
// NOT BACKED BY A REAL ENDPOINT YET (documented gap, Task 2d): Stream 1
// shipped a low-level `detectors.combination_matrix()` primitive (per-
// component top-K co-retrieval + client-side intersections) but no route
// wires it into a feature x domain count grid — the shape the screen2 mockup
// needs. These types/functions describe the UI-side shape CombinationMatrix.tsx
// renders; WhiteSpaceTab always passes null until a real endpoint exists.
// ---------------------------------------------------------------------------

export type WhitespaceDensityBand = 'white' | 'thin' | 'light' | 'active' | 'crowded' | 'saturated'

export interface WhitespaceMatrixCell {
  feature_id: string
  feature_label: string
  domain_id: string
  domain_label: string
  /** Raw thresholded-match count for this cell — informational only.
   * `density_band`/`is_white_space` are driven by `share` (Story #5305
   * Task #5324 design §5.3), never by this raw count directly, because a
   * scan-bounded neighbourhood's absolute counts are far too small for the
   * old fixed DENSITY_THRESHOLDS below to badge honestly. */
  count: number
  /** Normalised share (0-1) of this component's OWN total matched records
   * that fall in this domain — the real driver of density_band. Optional
   * only for backward compatibility with any cached pre-Task-#5324 cell
   * shape; every cell the real backend now returns carries it. */
  share?: number
  density_band: WhitespaceDensityBand
  is_white_space: boolean
}

/** design §5.3: "The density legend is re-derived from this data's real
 * distribution" — the server computes these bands from the ACTUAL matrix's
 * own share distribution (whitespace/matrix.py::_derive_legend), replacing
 * the fixed, corpus-volume-sized DENSITY_THRESHOLDS below for any matrix
 * that carries one. */
export interface WhitespaceMatrixLegendBand {
  band: WhitespaceDensityBand
  min_share: number
  label: string
}

export interface WhitespaceMatrixLegend {
  bands: WhitespaceMatrixLegendBand[]
  /** design §5.3: "The view states its coverage plainly" — always render
   * this verbatim next to the legend, never paraphrased. */
  coverage_note: string
}

export type WhitespaceMatrixStatus = 'complete' | 'no_components' | 'no_scan_yet' | 'no_domain_metadata'

/** design §5.3: the no-components state's consent-gated CTA — calls the
 * existing crosspollination decompose route and inherits ITS edit gate;
 * `edit_allowed` is the server's REAL check_permission(...,'edit') result
 * at read time, never inferred client-side. */
export interface WhitespaceMatrixDecomposeAction {
  edit_allowed: boolean
  route: string
  label: string
}

export interface WhitespaceMatrix {
  status: WhitespaceMatrixStatus
  features: { id: string; label: string }[]
  domains: { id: string; label: string }[]
  cells: WhitespaceMatrixCell[]
  /** null except when status === 'complete' — no re-derived legend exists
   * for a matrix with no cells to derive one from. */
  legend: WhitespaceMatrixLegend | null
  /** Present (non-null) only when status === 'no_components'. */
  decompose_action?: WhitespaceMatrixDecomposeAction | null
}

/** Density bands + their inclusive lower bound, per the screen2-matrix legend:
 * white 0-2, thin 3-9, light 10-29, active 30-99, crowded 100-299, saturated 300+. */
const DENSITY_THRESHOLDS: [WhitespaceDensityBand, number][] = [
  ['saturated', 300],
  ['crowded', 100],
  ['active', 30],
  ['light', 10],
  ['thin', 3],
  ['white', 0],
]

export function densityBandForCount(count: number): WhitespaceDensityBand {
  for (const [band, min] of DENSITY_THRESHOLDS) {
    if (count >= min) return band
  }
  return 'white'
}

/** Tailwind-free CSS colour pair for a density band cell, matching screen2's palette. */
const DENSITY_COLORS: Record<WhitespaceDensityBand, { background: string; color: string }> = {
  saturated: { background: '#0C4A6E', color: '#ffffff' },
  crowded: { background: '#0369A1', color: '#ffffff' },
  active: { background: '#38BDF8', color: '#062A3F' },
  light: { background: '#BAE6FD', color: '#0C4A6E' },
  thin: { background: '#E0F2FE', color: '#0369A1' },
  white: { background: '#ffffff', color: '#92400E' },
}

export function densityBandColor(band: WhitespaceDensityBand): { background: string; color: string } {
  return DENSITY_COLORS[band]
}

/** Pick a cell's band from the server-derived legend given its share — the
 * client-side mirror of whitespace/matrix.py::_band_for_share, used only
 * for cells re-rendered from a stale/partial payload; the backend already
 * stamps `density_band` on every real cell, so this is a fallback, never
 * the primary path. share === 0 is always 'white' regardless of legend. */
export function bandForShare(share: number, legend: WhitespaceMatrixLegend): WhitespaceDensityBand {
  if (share <= 0) return 'white'
  let band: WhitespaceDensityBand = 'white'
  for (const entry of legend.bands) {
    if (entry.band === 'white') continue
    if (share >= entry.min_share) band = entry.band
  }
  return band
}

/** Cells the matrix should badge as combination white space worth surfacing:
 * both halves exist (feature has other non-zero cells, domain has other
 * non-zero cells) but this specific pairing is empty/near-empty. */
export function whiteSpaceCells(cells: WhitespaceMatrixCell[]): WhitespaceMatrixCell[] {
  return cells.filter((c) => c.is_white_space)
}

/** Group flat cells into a row-major grid keyed by feature_id, in the order
 * features/domains are given — the shape CombinationMatrix.tsx renders directly. */
export function buildMatrixGrid(
  features: { id: string; label: string }[],
  domains: { id: string; label: string }[],
  cells: WhitespaceMatrixCell[],
): WhitespaceMatrixCell[][] {
  const byKey = new Map(cells.map((c) => [`${c.feature_id}::${c.domain_id}`, c]))
  return features.map((f) =>
    domains.map((d) => {
      const key = `${f.id}::${d.id}`
      const existing = byKey.get(key)
      if (existing) return existing
      // Missing cell (partial data) renders as an honest zero rather than a gap.
      return {
        feature_id: f.id,
        feature_label: f.label,
        domain_id: d.id,
        domain_label: d.label,
        count: 0,
        density_band: 'white' as WhitespaceDensityBand,
        is_white_space: true,
      }
    }),
  )
}

// ---------------------------------------------------------------------------
// Portfolio Synergy Matrix — Story #5115 Stream 5c (Task #5122)
// Pure grid-construction + banding logic, kept separate from the DENSE
// project-scoped CombinationMatrix helpers above: portfolio_synergy.py
// returns a SPARSE list of specific component pairs (only the top
// shared_themes/complementary_areas compute_pair_synergy found), not a full
// feature x domain grid — see types/whitespace.ts's PortfolioSynergyCell doc.
// ---------------------------------------------------------------------------

export interface PortfolioSynergyAxisEntry {
  id: string
  title: string
  project_id: string | null
}

export interface PortfolioSynergyGrid {
  features: PortfolioSynergyAxisEntry[]
  domains: PortfolioSynergyAxisEntry[]
  /** [feature_index][domain_index] -> the real cell, or null when this exact
   * combination was never scored — never backfilled as a fabricated 0. */
  grid: (PortfolioSynergyCell | null)[][]
}

/** Unique features (rows) / domains (columns) from the sparse cell list, each
 * first-seen order — deterministic given the API's own cell ordering. */
export function buildPortfolioSynergyGrid(cells: PortfolioSynergyCell[]): PortfolioSynergyGrid {
  const features: PortfolioSynergyAxisEntry[] = []
  const domains: PortfolioSynergyAxisEntry[] = []
  const featureIndex = new Map<string, number>()
  const domainIndex = new Map<string, number>()

  for (const c of cells) {
    if (!featureIndex.has(c.feature_component_id)) {
      featureIndex.set(c.feature_component_id, features.length)
      features.push({ id: c.feature_component_id, title: c.feature_title, project_id: c.feature_project_id })
    }
    if (!domainIndex.has(c.domain_component_id)) {
      domainIndex.set(c.domain_component_id, domains.length)
      domains.push({ id: c.domain_component_id, title: c.domain_title, project_id: c.domain_project_id })
    }
  }

  const grid: (PortfolioSynergyCell | null)[][] = features.map(() => domains.map(() => null))
  for (const c of cells) {
    const fi = featureIndex.get(c.feature_component_id)
    const di = domainIndex.get(c.domain_component_id)
    if (fi === undefined || di === undefined) continue
    grid[fi][di] = c
  }

  return { features, domains, grid }
}

/** Groups row indices by feature_project_id, preserving first-seen row order
 * — the per-project group headers SynergyMatrix.tsx renders (screen6's
 * "Tidal Micro-Turbine" style row groups). A null project_id (the backend's
 * component->project metadata lookup missed) groups under its own bucket
 * rather than silently joining a real project's group. */
export function groupSynergyRowsByProject(
  features: PortfolioSynergyAxisEntry[],
): { project_id: string | null; rowIndices: number[] }[] {
  const groups: { project_id: string | null; rowIndices: number[] }[] = []
  const byProject = new Map<string, number>()
  features.forEach((f, i) => {
    const key = f.project_id ?? '__unknown_project__'
    let groupIdx = byProject.get(key)
    if (groupIdx === undefined) {
      groupIdx = groups.length
      byProject.set(key, groupIdx)
      groups.push({ project_id: f.project_id, rowIndices: [] })
    }
    groups[groupIdx].rowIndices.push(i)
  })
  return groups
}

/** Confidence -> the SAME 6-band visual language densityBandColor() already
 * defines (screen2/screen6 share one colour palette), rebucketed for a 0-1
 * cosine confidence rather than a 0-300+ prior-art count. Thresholds sit
 * around CombinationMatrix's own sim >= 0.62 "real match" cutoff. */
const CONFIDENCE_THRESHOLDS: [WhitespaceDensityBand, number][] = [
  ['saturated', 0.85],
  ['crowded', 0.7],
  ['active', 0.55],
  ['light', 0.4],
  ['thin', 0.25],
  ['white', 0],
]

export function confidenceBand(confidence: number | null | undefined): WhitespaceDensityBand {
  const c = confidence ?? 0
  for (const [band, min] of CONFIDENCE_THRESHOLDS) {
    if (c >= min) return band
  }
  return 'white'
}

/**
 * The per-project colour, assigned by POSITION in the real `member_project_ids`
 * list the API returns, so a project's row-group dot and every column dot it
 * feeds always match.
 *
 * FOURTEEN colours, not eight, and none of them slate. The eight-colour version
 * was sized for the Synergy Matrix's own 8-project cap
 * (MAX_MEMBER_PROJECTS_FOR_MATRIX), but the entity map on S5 draws every
 * invention an entity has granted — twelve for Vanta Materials Group on the dev
 * estate — so the list wrapped and four pairs of inventions were drawn in
 * exactly the same colour, which is the one thing the colour is for.
 *
 * The old eighth slot was #94A3B8, the same slate the entity map's legend
 * reserves for "other entities · density only", so the twelfth invention was
 * drawn in the colour that means "not this entity". No entry here may be that
 * grey, and none may repeat: themeColours.test.ts asserts both.
 */
export const PROJECT_TAG_COLORS = [
  '#0EA5E9', // sky 500
  '#F43F5E', // rose 500
  '#14B8A6', // teal 500
  '#F59E0B', // amber 500
  '#6366F1', // indigo 500
  '#84CC16', // lime 500
  '#EC4899', // pink 500
  '#0891B2', // cyan 600
  '#7C3AED', // violet 600
  '#059669', // emerald 600
  '#EA580C', // orange 600
  '#1D4ED8', // blue 700
  '#BE123C', // rose 700
  '#4D7C0F', // lime 700
]

/**
 * Not one of the palette's colours, and deliberately so: this is the grey that
 * means "this is not one of the projects being coloured" — the density
 * background on the maps, and the fallback for an id the caller did not list.
 */
export const UNKNOWN_PROJECT_TAG_COLOR = '#94A3B8'

export function projectTagColor(projectId: string | null | undefined, memberProjectIds: string[]): string {
  if (!projectId) return UNKNOWN_PROJECT_TAG_COLOR
  const idx = memberProjectIds.indexOf(projectId)
  if (idx === -1) return UNKNOWN_PROJECT_TAG_COLOR
  return PROJECT_TAG_COLORS[idx % PROJECT_TAG_COLORS.length]
}

// ---------------------------------------------------------------------------
// Portfolio Atlas + Topology & Strategy — Story #5115 Stream 6f (Task #5131)
// Pure geometry/ranking logic, kept separate from React so it's directly
// vitest-covered without a DOM/canvas/SVG rendering harness.
// ---------------------------------------------------------------------------

/** `{project_id}:{document_id}` -> `project_id` — the SAME namespacing
 * convention every portfolio_* backend module in this story shares
 * (portfolio_projection.py's `_project_points`), split on the FIRST colon
 * only (a document_id could itself contain one). */
export function projectIdFromPortfolioRecordId(recordId: string): string | null {
  const idx = recordId.indexOf(':')
  if (idx === -1) return null
  return recordId.slice(0, idx)
}

export interface LayoutBounds {
  minX: number
  maxX: number
  maxY: number
  minY: number
}

/** Bounding box of every point in a portfolio layout. Returns a 1x1 box at
 * the origin for an empty layout so callers never divide by zero. */
export function computeLayoutBounds(layout: PortfolioMapLayout): LayoutBounds {
  const points = Object.values(layout)
  if (points.length === 0) return { minX: 0, maxX: 1, minY: 0, maxY: 1 }
  let minX = Infinity
  let maxX = -Infinity
  let minY = Infinity
  let maxY = -Infinity
  for (const [x, y] of points) {
    if (x < minX) minX = x
    if (x > maxX) maxX = x
    if (y < minY) minY = y
    if (y > maxY) maxY = y
  }
  if (minX === maxX) {
    minX -= 0.5
    maxX += 0.5
  }
  if (minY === maxY) {
    minY -= 0.5
    maxY += 0.5
  }
  return { minX, maxX, minY, maxY }
}

export interface AtlasViewport {
  width: number
  height: number
  padding: number
}

/** Builds a pure (x, y) -> [svgX, svgY] mapper: uniform scale (never
 * distorting aspect ratio), centered in the viewport, Y-flipped so the PCA
 * layout's "up" renders visually up rather than SVG's native down-is-positive. */
export function makeAtlasProjector(
  bounds: LayoutBounds,
  viewport: AtlasViewport,
): (point: [number, number]) => [number, number] {
  const spanX = bounds.maxX - bounds.minX || 1
  const spanY = bounds.maxY - bounds.minY || 1
  const drawableW = viewport.width - viewport.padding * 2
  const drawableH = viewport.height - viewport.padding * 2
  const scale = Math.min(drawableW / spanX, drawableH / spanY)
  const usedW = spanX * scale
  const usedH = spanY * scale
  const offsetX = viewport.padding + (drawableW - usedW) / 2
  const offsetY = viewport.padding + (drawableH - usedH) / 2
  return ([x, y]: [number, number]) => [
    offsetX + (x - bounds.minX) * scale,
    offsetY + (bounds.maxY - y) * scale,
  ]
}

/** Groups a portfolio layout's points by owning member project, in
 * `memberProjectIds` order (so an empty-but-entitled member still gets a
 * `[]` entry — the Atlas member list can then show "0 plotted" honestly
 * rather than silently omitting the row). */
export function groupPortfolioLayoutByProject(
  layout: PortfolioMapLayout,
  memberProjectIds: string[],
): Map<string, [number, number][]> {
  const byProject = new Map<string, [number, number][]>(memberProjectIds.map((id) => [id, []]))
  for (const [recordId, point] of Object.entries(layout)) {
    const projectId = projectIdFromPortfolioRecordId(recordId)
    if (!projectId) continue
    if (!byProject.has(projectId)) byProject.set(projectId, [])
    byProject.get(projectId)!.push(point)
  }
  return byProject
}

/** Component-wise mean of a set of 2-D points — the real per-project
 * centroid the Atlas marks with a star, never a fabricated position. */
export function computeCentroid2D(points: [number, number][]): [number, number] | null {
  if (points.length === 0) return null
  let sx = 0
  let sy = 0
  for (const [x, y] of points) {
    sx += x
    sy += y
  }
  return [sx / points.length, sy / points.length]
}

/** Traces the portfolio footprint's outer boundary loop from the topology
 * endpoint's `frontier_edges` — each is exactly one edge of the SAME outer
 * loop `whitespace/portfolio_topology.py::_trace_boundary_loops` already
 * traced server-side. Walks the edges into an ordered ring using the
 * identical "no unvisited neighbour -> stop rather than mis-trace" contract
 * as the backend's own tracer, so a portfolio whose boundary genuinely isn't
 * a simple 2-manifold yields `null` (no footprint drawn) instead of a wrong
 * shape. Returns the ordered vertex positions (closed implicitly — callers
 * append the first point again if their path renderer needs an explicit close). */
export function tracePortfolioFootprintLoop(
  frontierEdges: PortfolioTopologyFrontierEdge[],
  layout: PortfolioMapLayout,
): [number, number][] | null {
  if (frontierEdges.length < 3) return null

  const edgeKey = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`)
  const adjacency = new Map<string, string[]>()
  for (const edge of frontierEdges) {
    const [a, b] = edge.boundary_record_ids
    if (!a || !b) continue
    if (!adjacency.has(a)) adjacency.set(a, [])
    if (!adjacency.has(b)) adjacency.set(b, [])
    adjacency.get(a)!.push(b)
    adjacency.get(b)!.push(a)
  }

  const [start0, start1] = frontierEdges[0].boundary_record_ids
  if (!start0 || !start1) return null

  const visitedEdges = new Set<string>([edgeKey(start0, start1)])
  const loopIds = [start0]
  let current = start1
  let safety = 0
  while (current !== loopIds[0] && safety <= frontierEdges.length + 1) {
    loopIds.push(current)
    const neighbours = (adjacency.get(current) ?? []).filter((n) => !visitedEdges.has(edgeKey(current, n)))
    if (neighbours.length === 0) break
    const next = neighbours[0]
    visitedEdges.add(edgeKey(current, next))
    current = next
    safety += 1
  }

  if (current !== loopIds[0] || loopIds.length < 3) return null

  const points: [number, number][] = []
  for (const id of loopIds) {
    const point = layout[id]
    if (!point) return null
    points.push(point)
  }
  return points
}

/** disclosure/filters.py's STAGE_FIELDS stage names, verbatim from that
 * module's own docstring — never a made-up label. */
const DISCLOSURE_LEVEL_LABELS: Record<number, string> = {
  1: 'Anonymous match',
  2: 'Aggregated stats',
  3: 'Categorical profile',
  4: 'Pattern insights',
  5: 'Structured summary',
  6: 'Full collaboration',
}

export function disclosureLevelLabel(level: number): string {
  return DISCLOSURE_LEVEL_LABELS[level] ?? `Level ${level}`
}

export type RankedWhitespaceItemKind = PortfolioScoreRegion['type'] | 'synergy'

export interface RankedWhitespaceItem {
  /** e.g. 'H1', 'B2', 'F1', 'S1' — numbered per kind, in the order that kind
   * first appears once every item is sorted by score descending (matches
   * screen5/screen7's H1/S1/B1/F1/B2 labelling exactly). */
  id: string
  kind: RankedWhitespaceItemKind
  tagLabel: string
  score: number
  /** Absent for a synergy item — a synergy cell has no 2-D position, it's a
   * component-pair match, never a fabricated spatial placement. */
  centroid2d: [number, number] | null
  /** Full geometry for the Atlas: a hole's whole ordered boundary loop, a
   * bridge's 3 triangle vertices, a frontier edge's 2 endpoints — `null` for
   * a synergy item (no geometry) so the Atlas never fabricates a shape. */
  boundaryRecordIds: string[] | null
  memberProjectIds: string[]
  detail: string
  /** Story #6172 Task #6217 (F8): threaded from the source region's
   * PortfolioScoreRegion.insufficient_basis — absent (undefined) for a
   * synergy item, which has no such flag. Additive only; score is
   * unchanged. */
  insufficientBasis?: boolean
}

/** Single wording for the F8 insufficient-basis caveat, shared by every
 * consumer that renders ConfidenceGate for a flagged region — so
 * OwnShapeStrategyView (own scope) and TopologyStrategy (aggregator scope)
 * can never say two different things about the same region (the exact
 * failure class the 22 Aug 2026 three-views-disagreed incident was). */
export const PORTFOLIO_INSUFFICIENT_BASIS_REASON =
  "One or more of this region's sub-scores (momentum, enablement or synergy) came from a fallback default rather than real portfolio signal. The score shown is unchanged — treat it as provisional until more documents give the engine real signal to score."

const KIND_PREFIX: Record<RankedWhitespaceItemKind, string> = {
  hole: 'H',
  bridge: 'B',
  frontier_edge: 'F',
  synergy: 'S',
}

const KIND_TAG_LABEL: Record<RankedWhitespaceItemKind, string> = {
  hole: 'ENCLOSED HOLE',
  bridge: 'BRIDGE',
  frontier_edge: 'FRONTIER',
  synergy: 'SYNERGY',
}

function regionDetail(region: PortfolioScoreRegion, names: Map<string, string>): string {
  const projectNames = region.member_project_ids.map((id) => names.get(id) ?? id)
  const neighbourClause =
    region.full_space_density === 0
      ? 'no full-space neighbours found nearby'
      : `${region.full_space_density} full-space neighbour${region.full_space_density === 1 ? '' : 's'} found nearby`
  if (region.type === 'hole') {
    return `Enclosed gap the portfolio's own documents surround${
      projectNames.length ? ` — bordered by ${projectNames.join(', ')}` : ''
    }; ${neighbourClause}.`
  }
  if (region.type === 'bridge') {
    return `Thin, low-density connector between two of the portfolio's own clusters${
      projectNames.length === 2 ? ` (${projectNames[0]} ↔ ${projectNames[1]})` : ''
    }; ${neighbourClause}.`
  }
  return `Open direction on the outer edge of the portfolio's footprint; ${neighbourClause}.`
}

function topSynergyCellDetail(
  cell: PortfolioSynergyCell,
  names: Map<string, string>,
): string {
  const featureProject = cell.feature_project_id ? names.get(cell.feature_project_id) ?? cell.feature_project_id : 'an unknown project'
  const domainProject = cell.domain_project_id ? names.get(cell.domain_project_id) ?? cell.domain_project_id : 'an unknown project'
  const pct = cell.confidence != null ? Math.round(cell.confidence * 100) : null
  return (
    `Top synergy cell from the stitched matrix (${cell.feature_title} × ${cell.domain_title}` +
    `${pct != null ? `, ${pct}% component similarity` : ''}). Invisible to a single-project view — only this ` +
    `portfolio holds both ${featureProject} and ${domainProject}.`
  )
}

/** Merges Task #5130's WOS-P-scored topology regions with the single
 * strongest cross-project synergy cell (Task #5119/#5122) into ONE ranked
 * list, sorted by score descending — the shape screen7's "Portfolio white
 * space, ranked" list and screen5's atlas badges both need, sharing the
 * SAME ids so a badge in the Atlas and a row in Topology & Strategy always
 * refer to the same real finding. The "top synergy cell" is the single
 * highest-confidence cross-project cell in the whole matrix — a real
 * selection over real data, not an invented item. */
export function buildRankedWhitespaceItems(
  regions: PortfolioScoreRegion[],
  synergyCells: PortfolioSynergyCell[],
  synergyPairScores: PortfolioSynergyPairScore[],
  names: Map<string, string>,
): RankedWhitespaceItem[] {
  type Draft = {
    kind: RankedWhitespaceItemKind
    score: number
    centroid2d: [number, number] | null
    boundaryRecordIds: string[] | null
    memberProjectIds: string[]
    detail: string
    insufficientBasis?: boolean
  }
  const drafts: Draft[] = regions.map((region) => ({
    kind: region.type,
    score: region.wos_p_score,
    centroid2d: region.centroid_2d,
    boundaryRecordIds: region.boundary_record_ids,
    memberProjectIds: region.member_project_ids,
    detail: regionDetail(region, names),
    insufficientBasis: region.insufficient_basis,
  }))

  const topCell = [...synergyCells]
    .filter((c) => c.is_cross_project)
    .sort((a, b) => (b.confidence ?? -1) - (a.confidence ?? -1))[0]
  if (topCell) {
    const pair = synergyPairScores.find((p) => p.pair_key === topCell.pair_key)
    drafts.push({
      kind: 'synergy',
      score: pair?.overall_score ?? topCell.score ?? 0,
      centroid2d: null,
      boundaryRecordIds: null,
      memberProjectIds: [topCell.feature_project_id, topCell.domain_project_id].filter(
        (id): id is string => !!id,
      ),
      detail: topSynergyCellDetail(topCell, names),
    })
  }

  drafts.sort((a, b) => b.score - a.score)

  const seq: Record<RankedWhitespaceItemKind, number> = { hole: 0, bridge: 0, frontier_edge: 0, synergy: 0 }
  return drafts.map((d) => {
    seq[d.kind] += 1
    return {
      id: `${KIND_PREFIX[d.kind]}${seq[d.kind]}`,
      kind: d.kind,
      tagLabel: KIND_TAG_LABEL[d.kind],
      score: Math.round(d.score),
      centroid2d: d.centroid2d,
      boundaryRecordIds: d.boundaryRecordIds,
      memberProjectIds: d.memberProjectIds,
      detail: d.detail,
      insufficientBasis: d.insufficientBasis,
    }
  })
}

/** Purely templated "where to act next" copy — every clause maps to a real
 * field on a ranked item (id, tag, score, detail); nothing here is
 * Bedrock-narrated or invented (whitespace/synthesis.py is not wired into
 * any portfolio route — same documented gap as PortfolioSynergyCell). */
export function buildActionSequence(items: RankedWhitespaceItem[], max = 4): string[] {
  return items.slice(0, max).map((item, i) => {
    const verb = item.kind === 'synergy' ? 'Draft the joint concept for' : 'Act on'
    return `${i + 1}. ${verb} ${item.id} (score ${item.score}). ${item.detail}`
  })
}

export interface PortfolioShapeTallies {
  holes: number
  bridges: number
  frontierEdges: number
  memberProjectCount: number
  crossProjectSynergyCells: number
  discardedCandidateCount: number
}

/** Real, derived counts for the "Portfolio shape at a glance" / footprint
 * tiles — every number here comes straight off an already-fetched API
 * response, never invented (see the module-level "themes"/"overlap %"/
 * "encroachment pressure" gap note — those three mockup tiles have no
 * backing endpoint and are surfaced as an explicit gap in the component,
 * not approximated here). */
export function buildPortfolioShapeTallies(
  holeCount: number,
  bridgeCount: number,
  frontierEdgeCount: number,
  memberProjectCount: number,
  crossProjectSynergyCells: number,
  discardedCandidateCount: number,
): PortfolioShapeTallies {
  return {
    holes: holeCount,
    bridges: bridgeCount,
    frontierEdges: frontierEdgeCount,
    memberProjectCount,
    crossProjectSynergyCells,
    discardedCandidateCount,
  }
}

// ---------------------------------------------------------------------------
// Portfolio Overlap — Story #5305 (Task #5317). Pure formatting/geometry
// logic only; the React components own fetching, polling and lifecycle
// display. Design §5.1's four fixed anchor phrasings are reproduced here
// verbatim so every surface that names a number quotes the identical words.
// ---------------------------------------------------------------------------

/** Design §5.1's fourth anchor phrasing, verbatim — overlap groups' single
 * number is a SHARE, not a similarity, so it gets its own sentence rather
 * than reusing the region/probe wording. */
export function overlapStrongestPairAnchor(coefficient: number): string {
  return `Strongest pair: ${coefficient} — the share of the smaller project's documents with a match in the other`
}

/** Design §5.1's third anchor phrasing, verbatim — one real document-pair
 * evidence row's similarity (Part F's evidence table). */
export function overlapDocumentPairAnchor(cosinePct: number): string {
  return `How close these two documents are: ${cosinePct}`
}

/** Design §6.2: "based on only N document(s) — treat as a lead, not a
 * verdict" — the worth-a-look card's own caption, using the SMALLER side's
 * comparable count (the count the floor test itself failed on). */
export function overlapWorthALookCaption(comparableSmaller: number): string {
  const noun = comparableSmaller === 1 ? 'document' : 'documents'
  return `Based on only ${comparableSmaller} ${noun} — treat as a lead, not a verdict`
}

const OVERLAP_TIER_COLORS: Record<OverlapTier, { background: string; color: string }> = {
  strong: { background: '#FEF3C7', color: '#92400E' },
  moderate: { background: '#E0F2FE', color: '#0369A1' },
  weak: { background: '#F1F5F9', color: '#64748B' },
}

/** `null` (a score below the weak-signal floor) gets the same neutral
 * treatment as a 'weak' pair — never an invented colour. */
export function overlapTierColor(tier: OverlapTier | null): { background: string; color: string } {
  return tier ? OVERLAP_TIER_COLORS[tier] : { background: '#F1F5F9', color: '#64748B' }
}

// The group record served today (handler.py's _get_portfolio_overlap) is
// ids-and-score only — no tier/relationship field. These thresholds are
// whitespace/overlap_score.py's own published, real constants
// (STRONG_TIER_MIN/MODERATE_TIER_MIN/WEAK_TIER_MIN) — re-deriving the same
// formula client-side from a real `score` field is not a fabrication, it's
// applying the documented rule; the moment the backend starts returning a
// computed tier/headline directly, these should read from it instead.
const OVERLAP_STRONG_TIER_MIN = 60
const OVERLAP_MODERATE_TIER_MIN = 40
const OVERLAP_WEAK_TIER_MIN = 25

/** Same boundaries overlap_score.py's tier_for_coefficient uses. `null`
 * below the weak-signal floor — a group never reaches the UI at that score
 * (backend excludes it from `groups` entirely), so this is mostly for
 * completeness/tests. */
export function overlapTierForScore(score: number): OverlapTier | null {
  if (score >= OVERLAP_STRONG_TIER_MIN) return 'strong'
  if (score >= OVERLAP_MODERATE_TIER_MIN) return 'moderate'
  if (score >= OVERLAP_WEAK_TIER_MIN) return 'weak'
  return null
}

/** A plain-English verdict derived deterministically from `score` and the
 * member count — design §5.1's own rule ("the sentence is computed, so it
 * can never disagree with the number"). This is NOT the same sentence
 * overlap_score.py's score_pair() computes per PAIR (that needs the
 * smaller/larger share split, which the served group record does not
 * carry) — it is a coarser, group-level reading of the same score band,
 * honestly scoped to what the real payload supports. */
export function overlapRelationshipHeadline(score: number, memberCount: number): string {
  const tier = overlapTierForScore(score)
  if (tier === 'strong') return memberCount > 2 ? 'Substantially the same work, across several projects' : 'Substantially the same work'
  if (tier === 'moderate') return 'Significant shared ground'
  if (tier === 'weak') return 'Weak signal'
  return 'Worth a look'
}

/** The ONE uniform message every overlap-group surface renders for an
 * unknown, de-entitled or decayed group id — byte-identical to the backend's
 * own copy (whitespace/overlap_notify.py's STALE_GROUP_MESSAGE and every
 * 404 handler.py's portfolio_overlap routes share). Story #5305 Task #5318
 * AC: "renders identically for unknown/de-entitled/decayed ids" — one
 * string, used everywhere a group-scoped call 404s, so no surface can drift
 * from another's wording. */
export const OVERLAP_STALE_GROUP_MESSAGE =
  'This overlap group is no longer available — groups change as portfolios and access change.'

/** "3 projects · Strongest pair: 93 — …" (screenB mockup's group-card meta
 * line, minus the entity/matched-pair-count clauses the served payload does
 * not carry — see the module-level DATA GAPS note in types/whitespace.ts). */
export function overlapGroupMetaLine(group: OverlapGroup): string {
  const projectCount = group.member_project_ids.length
  const projectNoun = projectCount === 1 ? 'project' : 'projects'
  return `${projectCount} ${projectNoun} · ${overlapStrongestPairAnchor(group.score)}`
}

/** How long ago a completed run finished, in the mockup's own words
 * ("Computed 18 min ago"). Rounds down to the minute; anything under a
 * minute reads as "just now" rather than "0 min ago". */
export function formatComputedAgo(completedAt: string | null, now: number = Date.now()): string {
  if (!completedAt) return 'not yet computed'
  const completedMs = Date.parse(completedAt)
  if (Number.isNaN(completedMs)) return 'not yet computed'
  const minutes = Math.floor((now - completedMs) / 60000)
  if (minutes < 1) return 'Computed just now'
  if (minutes === 1) return 'Computed 1 min ago'
  if (minutes < 60) return `Computed ${minutes} min ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `Computed ${hours} hr${hours === 1 ? '' : 's'} ago`
  const days = Math.floor(hours / 24)
  return `Computed ${days} day${days === 1 ? '' : 's'} ago`
}

export interface OverlapFootprintEllipse {
  project_id: string
  cx: number
  cy: number
  rx: number
  ry: number
}

/** One member project's footprint ellipse for the overlap mini-map —
 * centroid plus the RMS spread of its own points along each axis (never a
 * convex hull; the mockup's simplified-reading caption is honest about
 * that). A project with fewer than 2 points renders as `null` (no
 * meaningful spread to draw) rather than a fabricated dot-sized ellipse. */
export function computeFootprintEllipse(
  projectId: string,
  points: [number, number][],
): OverlapFootprintEllipse | null {
  if (points.length < 2) return null
  const centroid = computeCentroid2D(points)
  if (!centroid) return null
  const [cx, cy] = centroid
  let sumSqX = 0
  let sumSqY = 0
  for (const [x, y] of points) {
    sumSqX += (x - cx) ** 2
    sumSqY += (y - cy) ** 2
  }
  const rx = Math.sqrt(sumSqX / points.length)
  const ry = Math.sqrt(sumSqY / points.length)
  // A floor so a tightly-clustered project (near-zero spread) still draws a
  // visible ellipse rather than collapsing to an invisible point.
  const MIN_RADIUS = 0.02
  return { project_id: projectId, cx, cy, rx: Math.max(rx, MIN_RADIUS), ry: Math.max(ry, MIN_RADIUS) }
}

/** Every member project's footprint ellipse, built from the portfolio map
 * layout filtered to this group's members — reuses groupPortfolioLayoutByProject
 * (the same atlas utility PortfolioAtlas.tsx already relies on) so the two
 * views can never disagree about which point belongs to which project. */
export function computeGroupFootprints(
  layout: PortfolioMapLayout,
  memberProjectIds: string[],
): OverlapFootprintEllipse[] {
  const byProject = groupPortfolioLayoutByProject(layout, memberProjectIds)
  const ellipses: OverlapFootprintEllipse[] = []
  for (const projectId of memberProjectIds) {
    const ellipse = computeFootprintEllipse(projectId, byProject.get(projectId) ?? [])
    if (ellipse) ellipses.push(ellipse)
  }
  return ellipses
}

/** Bounds across every footprint ellipse (including their radii, not just
 * centres) so the mini-map's projector never clips a wide ellipse at the
 * viewport edge. Falls back to computeLayoutBounds's own 1x1 default when
 * there are no ellipses to bound. */
export function computeFootprintBounds(ellipses: OverlapFootprintEllipse[]): LayoutBounds {
  if (ellipses.length === 0) return { minX: 0, maxX: 1, minY: 0, maxY: 1 }
  let minX = Infinity
  let maxX = -Infinity
  let minY = Infinity
  let maxY = -Infinity
  for (const e of ellipses) {
    minX = Math.min(minX, e.cx - e.rx)
    maxX = Math.max(maxX, e.cx + e.rx)
    minY = Math.min(minY, e.cy - e.ry)
    maxY = Math.max(maxY, e.cy + e.ry)
  }
  return { minX, maxX, minY, maxY }
}

// ---------------------------------------------------------------------------
// Story #5305 Stream 14c (Task #5319) — surfacing, Atlas Overlap lens,
// Synergy↔Overlap reconciliation, and the P1 riders (scan-status chip,
// portfolio-side band names/0-100 scale).
// ---------------------------------------------------------------------------

/** design §5.1's five-band distance ladder, verbatim — non-overlapping
 * integer ranges, "distance words, deliberately not place words". Ordered
 * closest-first so `bandForInteger` can return on first match. */
export const SIMILARITY_BAND_LADDER: { name: string; lo: number; hi: number }[] = [
  { name: 'Very close', lo: 85, hi: 100 },
  { name: 'Close', lo: 72, hi: 84 },
  { name: 'Moderately close', lo: 62, hi: 71 },
  { name: 'Further out', lo: 45, hi: 61 },
  { name: 'Far afield', lo: 0, hi: 44 },
]

/** Which named band a displayed integer (0-100) falls in — clamps out-of-
 * range input to the ladder's own ends rather than returning nothing, since
 * every real cosine value maps into [0, 100]. */
export function similarityBandForInteger(pct: number): string {
  const clamped = Math.max(0, Math.min(100, pct))
  return (SIMILARITY_BAND_LADDER.find((b) => clamped >= b.lo && clamped <= b.hi) ?? SIMILARITY_BAND_LADDER[SIMILARITY_BAND_LADDER.length - 1]).name
}

/** design §5.1: "band membership is computed on the displayed integer — the
 * floor of 100 × the value" — and the printed ranges must be non-overlapping,
 * which for a band tuple means the SAME upper-edge-exclusive treatment
 * `bandPrintedRange` already uses (a band's raw upper edge is the next
 * band's lower edge, e.g. Moderately close's stored (0.62, 0.72) must print
 * as 62-71, not 62-72, or its top integer collides with Close's 72-84). A
 * watch's stored `band` is always one of ANNULUS_BANDS' five exact tuples
 * (whitespace/handler.py's `_name_region` matches it verbatim against the
 * caller's own scan) — this is the same function, not a second
 * implementation that could drift from it. */
export function similarityIntegerRange(band: [number, number]): [number, number] {
  return bandPrintedRange(band)
}

/** The portfolio watch panel's (and any other watch row's) band label —
 * design §5.2: "watch rows everywhere adopt the band names and integer
 * ranges, including the portfolio watch panel that today prints raw
 * cosine." Every real watch band is one exact ANNULUS_BANDS tuple, so
 * loBand/hiBand always agree — the dual-name join below is a defensive
 * fallback only, for a malformed/legacy stored range that genuinely spans
 * two named bands, so such a row still renders something readable instead
 * of silently picking one name and hiding the other. */
export function similarityBandRangeLabel(band: [number, number]): string {
  const [lo, hi] = similarityIntegerRange(band)
  const loBand = similarityBandForInteger(lo)
  const hiBand = similarityBandForInteger(hi)
  const name = loBand === hiBand ? loBand : `${loBand} / ${hiBand}`
  return `${name} ${lo}–${hi}`
}

/** Overview surfacing card: "N scored" vs design §6.2's separate "N leads
 * worth a look" line — a worth-a-look group is EXCLUDED from the scored
 * count (design: "excluded from... scored-group Overview counts"), never
 * double-counted. `worth_a_look` is undefined on any group record persisted
 * before this stream shipped — undefined reads as a scored group (the
 * field's own documented default), never silently dropped. */
export function overlapScoredGroupCount(groups: OverlapGroup[]): number {
  return groups.filter((g) => !g.worth_a_look).length
}

/** The companion count for the same card's "N leads worth a look" line. */
export function overlapWorthALookGroupCount(groups: OverlapGroup[]): number {
  return groups.filter((g) => g.worth_a_look).length
}

/** Cross-reference for Synergy↔Overlap reconciliation (design §6.3): the
 * scored (>=40, i.e. non-worth_a_look — group formation itself already
 * gates at coefficient 40, so every persisted non-worth_a_look group
 * qualifies) Overlap group, if any, whose membership covers BOTH of a
 * synergy cell's two projects. Returns null when neither project pair nor
 * any 3+ member group's pair covers them — a synergy cell with no matching
 * Overlap group is simply not reconciled, never fabricated. */
export function overlapGroupForProjectPair(
  groups: OverlapGroup[],
  projectAId: string | null | undefined,
  projectBId: string | null | undefined,
): OverlapGroup | null {
  if (!projectAId || !projectBId || projectAId === projectBId) return null
  return (
    groups.find(
      (g) => !g.worth_a_look && g.member_project_ids.includes(projectAId) && g.member_project_ids.includes(projectBId),
    ) ?? null
  )
}

/** The reverse of overlapGroupForProjectPair — every cross-project synergy
 * cell whose two projects are BOTH members of the given Overlap group, so
 * OverlapView's group detail panel can cross-link back into the Synergy
 * Matrix (design §6.3: "Synergy↔Overlap reconciliation annotations +
 * cross-links both ways"). A group with no matching synergy cell returns an
 * empty array, never a fabricated one. */
export function synergyCellsForGroupMembers(
  cells: PortfolioSynergyCell[],
  memberProjectIds: string[],
): PortfolioSynergyCell[] {
  const members = new Set(memberProjectIds)
  return cells.filter(
    (c) =>
      c.is_cross_project &&
      !!c.feature_project_id &&
      !!c.domain_project_id &&
      members.has(c.feature_project_id) &&
      members.has(c.domain_project_id),
  )
}

/** Elapsed time since a stage's `started_at` timestamp, in the mockup's own
 * short clock form ("2m 14s", "58s") — ScanStatusChip's own ticking display.
 * `null` for a missing/unparseable timestamp so the chip can render the
 * stage alone rather than "NaNm NaNs". */
export function formatElapsedShort(startedAt: string | null | undefined, now: number = Date.now()): string | null {
  if (!startedAt) return null
  const startMs = Date.parse(startedAt)
  if (Number.isNaN(startMs)) return null
  const totalSeconds = Math.max(0, Math.floor((now - startMs) / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`
}

/**
 * id -> display name lookup for the portfolio analytics. Reads the FULL
 * entitled set from AggregatorPortfolioData.project_names and overlays the
 * grant-scoped `projects` rows on top. The analytics — Overlap, Synergy
 * Matrix, Atlas, Portfolio Watch — render member ids drawn from a wider set
 * than `projects`, so every name map must go through here or ~half the labels
 * fall back to a raw UUID (Story #5305 CoWork re-test). Callers still do
 * `map.get(id) ?? id` for the rare id neither source covers.
 */
export function portfolioProjectNames(
  data?: { projects?: { project_id: string; name?: string }[]; project_names?: Record<string, string> } | null,
): Map<string, string> {
  const m = new Map<string, string>(Object.entries(data?.project_names ?? {}))
  for (const p of data?.projects ?? []) {
    if (p?.name) m.set(p.project_id, p.name)
  }
  return m
}
